import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function SignupConfirmation() {
  const navigate = useNavigate();
  const [calendlyLoaded, setCalendlyLoaded] = useState(false);

  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => setCalendlyLoaded(true);
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '40px',
      maxWidth: '900px',
      width: '100%',
      boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
    },
    logo: {
      textAlign: 'center',
      marginBottom: '30px'
    },
    logoText: {
      fontSize: '36px',
      fontWeight: 'bold',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '10px'
    },
    successIcon: {
      textAlign: 'center',
      fontSize: '64px',
      marginBottom: '20px'
    },
    title: {
      fontSize: '32px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '16px',
      textAlign: 'center'
    },
    message: {
      fontSize: '18px',
      color: '#4b5563',
      marginBottom: '30px',
      textAlign: 'center',
      lineHeight: '1.6'
    },
    infoBox: {
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      padding: '24px',
      marginBottom: '30px',
      border: '1px solid #e5e7eb'
    },
    infoTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '16px'
    },
    stepsList: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    stepItem: {
      fontSize: '15px',
      color: '#4b5563',
      marginBottom: '12px',
      paddingLeft: '32px',
      position: 'relative',
      lineHeight: '1.5'
    },
    stepNumber: {
      position: 'absolute',
      left: 0,
      top: 0,
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      fontSize: '12px',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    calendlySection: {
      marginTop: '40px',
      paddingTop: '40px',
      borderTop: '2px solid #e5e7eb'
    },
    calendlyTitle: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '12px',
      textAlign: 'center'
    },
    calendlySubtitle: {
      fontSize: '16px',
      color: '#6b7280',
      marginBottom: '30px',
      textAlign: 'center',
      lineHeight: '1.6'
    },
    calendlyWidget: {
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      backgroundColor: 'white',
      minHeight: '700px'
    },
    loadingContainer: {
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      padding: '60px 20px',
      textAlign: 'center',
      minHeight: '700px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    },
    spinner: {
      border: '4px solid #e5e7eb',
      borderTop: '4px solid #667eea',
      borderRadius: '50%',
      width: '50px',
      height: '50px',
      animation: 'spin 1s linear infinite',
      marginBottom: '20px'
    },
    loadingText: {
      fontSize: '16px',
      color: '#6b7280',
      fontWeight: '500'
    },
    backButton: {
      marginTop: '30px',
      textAlign: 'center'
    },
    button: {
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#667eea',
      backgroundColor: 'transparent',
      border: '2px solid #667eea',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s'
    }
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={styles.card}>
        <div style={styles.logo}>
          <div style={styles.logoText}>SkipEye</div>
        </div>

        <div style={styles.successIcon}>✓</div>

        <div style={styles.title}>Thank You!</div>
        <div style={styles.message}>
          We've received your trial request and are excited to help transform your waste management operations.
        </div>

        <div style={styles.infoBox}>
          <div style={styles.infoTitle}>What happens next?</div>
          <ul style={styles.stepsList}>
            <li style={styles.stepItem}>
              <div style={styles.stepNumber}>1</div>
              Our team will review your request within 24 hours
            </li>
            <li style={styles.stepItem}>
              <div style={styles.stepNumber}>2</div>
              You'll receive an email with your account setup link
            </li>
            <li style={styles.stepItem}>
              <div style={styles.stepNumber}>3</div>
              Complete your profile and start your 14-day free trial
            </li>
            <li style={styles.stepItem}>
              <div style={styles.stepNumber}>4</div>
              Get personalized onboarding support from our team
            </li>
          </ul>
        </div>

        <div style={styles.calendlySection}>
          <div style={styles.calendlyTitle}>
            Book Your Free Intro Call
          </div>
          <div style={styles.calendlySubtitle}>
            Want to learn more? Schedule a 30-minute call with our team to discuss your specific needs and see SkipEye in action.
          </div>

          {/* Calendly inline widget */}
          {calendlyLoaded ? (
            <div
              className="calendly-inline-widget"
              data-url="https://calendly.com/skipeye-info/30min?primary_color=667eea"
              style={{
                minWidth: '320px',
                height: '700px',
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            />
          ) : (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner}></div>
              <div style={styles.loadingText}>Loading calendar...</div>
            </div>
          )}
        </div>

        <div style={styles.backButton}>
          <button
            style={styles.button}
            onClick={() => navigate('/')}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#667eea';
              e.target.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#667eea';
            }}
          >
            Return to Home
          </button>
        </div>
      </div>
    </div>
  );
}

export default SignupConfirmation;
